/* Live "something changed" stream from the server (Redis → /api/sync/events/).
 *
 * One EventSource per session. Every change drops the in-memory API cache
 * (src/services/apiCache.js — pages then reopen instantly with exact data),
 * broadcasts `kipara:changes` on window (pages can refetch with
 * useLiveRefresh below) and lets the offline product copy refresh (throttled).
 * On the desktop app the VPS stream is consumed by the local engine; the app
 * long-polls the engine's /api/sync/local/changes/ instead.
 */
import { useEffect } from 'react';
import { apiClient } from './services/authService';
import { invalidateApiCache, isOwnChange, setChangeStreamLive } from './services/apiCache';
import { refreshProductCache } from './offline/productCache';

const RETRY_MS = 15000;
let source = null;
let timer = null;
let lastSeq = null;
let streamLive = false;

function apiBase() {
  const base = apiClient.defaults.baseURL || '/api/';
  return base.endsWith('/') ? base : `${base}/`;
}

function setLive(on) {
  if (on === streamLive) return;
  streamLive = on;
  setChangeStreamLive(on);
}

function announce(seq, own = false) {
  if (!own) invalidateApiCache(); // own write: dropped already when it returned
  window.dispatchEvent(new CustomEvent('kipara:changes', { detail: { seq } }));
  refreshProductCache(); // throttled: the offline copy needn't follow every sale
}

const isDesktop = () => !!(window.kipara && !window.kipara.mobile);

function connect() {
  const token = localStorage.getItem('token');
  if (!token || typeof EventSource === 'undefined') return;
  if (isDesktop()) { startEngineWatch(); return; }
  try { if (source) source.close(); } catch { /* ignore */ }
  setLive(false);
  source = new EventSource(`${apiBase()}sync/events/?token=${encodeURIComponent(token)}`);
  source.addEventListener('hello', (e) => {
    let seq = null;
    try { seq = JSON.parse(e.data).seq; } catch { /* ignore */ }
    // Reconnected after a gap: whatever changed meanwhile was not announced.
    if (lastSeq !== null && seq !== lastSeq) announce(seq);
    lastSeq = seq;
    setLive(true);
  });
  source.addEventListener('changes', (e) => {
    let seq = null;
    let own = false;
    try {
      const msg = JSON.parse(e.data);
      seq = msg.seq;
      own = isOwnChange(msg.id);
    } catch { /* ignore */ }
    // Every message is a write on the server, even when the seq did not move
    // (tables outside the change feed): always drop the cache — unless it is
    // the echo of a write made from this app.
    if (!own) invalidateApiCache();
    if (seq !== null && seq === lastSeq) return;
    lastSeq = seq;
    announce(seq, own);
  });
  source.onerror = () => {
    setLive(false); // until the next "hello"
    // EventSource reconnects by itself; a 401/503 closes it for good, retry later.
    if (source && source.readyState === EventSource.CLOSED) {
      clearTimeout(timer);
      timer = setTimeout(connect, RETRY_MS);
    }
  };
}

// ---------------------------------------------------------------- desktop app
// The engine answers /api/sync/local/changes/?since=N as soon as the data the
// app reads may have changed (VPS announcement, replica pull, online/offline
// switch). An older engine without it answers 404: the cache then only merges
// bursts of identical requests (no live signal).
let engineWatching = false;

async function startEngineWatch() {
  if (engineWatching) return;
  engineWatching = true;
  let gen = null;
  let failures = 0;
  while (engineWatching) {
    try {
      const res = await apiClient.get('sync/local/changes/', {
        params: { since: gen === null ? -1 : gen, wait: gen === null ? 0 : 25 },
        timeout: 35000,
        cache: false,
      });
      failures = 0;
      const next = res.data && res.data.gen;
      if (gen !== null && next !== gen) announce(next);
      gen = next;
      setLive(!!(res.data && res.data.live));
    } catch (err) {
      setLive(false);
      if (err && err.response && err.response.status === 404) { engineWatching = false; return; }
      failures += 1;
      gen = null; // re-sync (and drop the cache) once the engine answers again
      invalidateApiCache();
      const delay = Math.min(2000 * failures, 15000);
      await new Promise((r) => { setTimeout(r, delay); });
    }
  }
}

let started = false;
export function startRealtime() {
  if (started || typeof window === 'undefined') return;
  started = true;
  connect();
  // Token appears after login (same tab) or changes in another tab.
  window.addEventListener('storage', (e) => { if (e.key === 'token') connect(); });
  window.addEventListener('kipara:login', connect);
  document.addEventListener('visibilitychange', () => {
    if (isDesktop()) return;
    if (document.visibilityState === 'visible' && (!source || source.readyState === EventSource.CLOSED)) connect();
  });
}

/** React hook: run `callback` whenever the server reports a change. */
export function useLiveRefresh(callback) {
  useEffect(() => {
    const handler = (e) => callback(e.detail);
    window.addEventListener('kipara:changes', handler);
    return () => window.removeEventListener('kipara:changes', handler);
  }, [callback]);
}
